import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于日本女优大全',
    description: '日本女优大全致力于为影迷提供最全面的日本演艺女优参考资源。',
    url: 'https://www.ribennvyou.com/about',
    inLanguage: 'zh-CN',
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      const formUrl = 'https://readdy.ai/api/form/d9srsgtlbrt1qcgaj960';
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        try {
          const parsed = JSON.parse(responseText);
          if (parsed?.code === 'OK') {
            setFormStatus('success');
            setFormMessage('感谢您的留言，我们会尽快回复！');
            form.reset();
          } else {
            setFormStatus('error');
            setFormMessage(parsed?.meta?.message || '提交失败，请稍后重试');
          }
        } catch {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快回复！');
          form.reset();
        }
      } else {
        setFormStatus('error');
        try {
          const parsed = JSON.parse(responseText);
          setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
        } catch {
          setFormMessage('提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const stats = [
    { value: '12+', label: '收录女优', icon: 'ri-user-star-line' },
    { value: '6', label: '分类栏目', icon: 'ri-folders-line' },
    { value: '24h', label: '更新频率', icon: 'ri-refresh-line' },
    { value: '100%', label: '免费使用', icon: 'ri-heart-line' },
  ];

  const features = [
    {
      icon: 'ri-database-2-line',
      title: '全面资料库',
      desc: '收录数千位日本演艺女优的详尽资料，包括出道背景、代表作品、获奖记录等全方位信息。',
    },
    {
      icon: 'ri-search-eye-line',
      title: '便捷搜索',
      desc: '支持中日文双语搜索，按类型、年份、人气等多维度筛选，快速定位目标女优。',
    },
    {
      icon: 'ri-timer-flash-line',
      title: '实时更新',
      desc: '编辑团队每日追踪日本演艺圈动态，第一时间更新女优最新作品和新闻信息。',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多端适配',
      desc: '完美适配PC端和移动端，随时随地查阅女优资料，浏览体验流畅舒适。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 日本女优大全 | 品牌介绍与联系方式"
        description="日本女优大全致力于为影迷提供最全面的日本演艺女优参考资源。了解我们的品牌故事、核心优势和联系方式。"
        keywords="日本女优大全,关于我们,联系方式,日本女优资料库"
        canonicalUrl="https://www.ribennvyou.com/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-14 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于日本女优大全
            </h1>
            <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              最全面的日本演艺女优参考资源，致力于为全球影迷提供专业、准确、及时的女优信息查询服务
            </p>
          </section>

          <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-5 bg-background-100 rounded-xl">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div className="text-2xl font-heading font-bold text-foreground-950">{stat.value}</div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-semibold text-foreground-950 text-center mb-10">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((feature) => (
                <div key={feature.title} className="flex gap-4 p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">{feature.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-semibold text-foreground-950 text-center mb-10">
              品牌声誉
            </h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <div className="max-w-2xl mx-auto space-y-4 text-sm text-foreground-700 leading-relaxed">
                <p>
                  日本女优大全自成立以来，始终秉持"专业、全面、及时"的理念，
                  致力于为日本演艺文化爱好者打造最优质的女优资料查询平台。
                </p>
                <p>
                  我们的编辑团队由多名资深的日本演艺圈观察者和媒体从业者组成，
                  凭借多年的行业经验和专业知识，确保每一条信息的准确性和权威性。
                </p>
                <p>
                  我们与日本多家娱乐媒体和事务所保持着良好的合作关系，
                  能够在第一时间获取最新的女优动态信息。
                </p>
                <p>
                  未来，我们将继续扩展内容覆盖范围，优化用户体验，
                  为全球影迷提供更加丰富和便捷的日本女优信息查询服务。
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-foreground-950 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">电子邮件</h3>
                      <p className="text-xs text-foreground-600">contact@ribennvyou.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">所在地</h3>
                      <p className="text-xs text-foreground-600">日本东京都</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-0.5">工作时间</h3>
                      <p className="text-xs text-foreground-600">周一至周五 10:00 - 18:00（日本时间）</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} id="contact-form" data-readdy-form="" className="bg-background-100 rounded-xl p-5 md:p-6 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送留言
                  </button>

                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 p-3 bg-accent-50 text-accent-800 rounded-lg text-xs">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line"></i>
                      </div>
                      {formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-xs">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}